import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { RotateCcw, Trash2 } from "lucide-react";
import { useConfirm } from "@/components/admin/ConfirmDialog";

export const Route = createFileRoute("/_authenticated/admin/lixeira")({
  component: AdminTrash,
});

function daysLeft(deletedAt: string | null) {
  if (!deletedAt) return "—";
  const days = 30 - Math.floor((Date.now() - new Date(deletedAt).getTime()) / 86400000);
  if (days <= 0) return "a qualquer momento";
  return days === 1 ? "em 1 dia" : `em ${days} dias`;
}

type Row = { id: string; slug: string; h1_title: string; deleted_at: string | null };

function AdminTrash() {
  const [rows, setRows] = useState<Row[]>([]);
  const { confirm, confirmDialog } = useConfirm();
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("posts")
      .select("id,slug,h1_title,deleted_at")
      .not("deleted_at", "is", null)
      .order("deleted_at", { ascending: false });
    setRows((data ?? []) as unknown as Row[]);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  async function restore(row: Row) {
    const { error } = await supabase.from("posts").update({ deleted_at: null }).eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success("Artigo restaurado.");
    void load();
  }

  async function destroy(row: Row) {
    const ok = await confirm({
      title: "Excluir artigo definitivamente?",
      description: `"${row.h1_title}" será apagado para sempre. Esta ação não pode ser desfeita.`,
      confirmLabel: "Excluir para sempre",
    });
    if (!ok) return;
    const { error } = await supabase.from("posts").delete().eq("id", row.id);
    if (error) return toast.error(error.message);
    toast.success("Artigo excluído.");
    void load();
  }

  return (
    <div className="kk-cms__page">
      <div className="kk-cms__head">
        <div>
          <h1>Lixeira</h1>
          <p>Artigos removidos podem ser restaurados ou excluídos definitivamente. Itens na lixeira há mais de <strong>30 dias</strong> são apagados automaticamente.</p>
        </div>
      </div>

      <div className="kk-cms__table-wrap">
        <table className="kk-cms__table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Excluído em</th>
              <th>Apaga em</th>
              <th style={{ textAlign: "right" }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="kk-cms__empty">
                  Carregando…
                </td>
              </tr>
            ) : null}
            {!loading && !rows.length ? (
              <tr>
                <td colSpan={4} className="kk-cms__empty">
                  A lixeira está vazia.
                </td>
              </tr>
            ) : null}
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.h1_title}
                  <span className="kk-cms__slug">/{row.slug}</span>
                </td>
                <td>{row.deleted_at ? new Date(row.deleted_at).toLocaleDateString("pt-BR") : "—"}</td>
                <td>{daysLeft(row.deleted_at)}</td>
                <td>
                  <div className="kk-cms__row-actions">
                    <button type="button" className="kk-icon-btn" title="Restaurar" onClick={() => restore(row)}>
                      <RotateCcw size={16} />
                    </button>
                    <button type="button" className="kk-icon-btn kk-icon-btn--danger" title="Excluir definitivamente" onClick={() => destroy(row)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {confirmDialog}
    </div>
  );
}
